// useSocketInit.js

import { useState, useEffect, useRef, useMemo } from "react";
import { initSocket, getSocket } from "../components/socketManager";
import { saveMessage, saveScheduledMessage, deleteScheduledMessage, deleteChatMessages, } from "../db/fsDB";
import {
  fetchPrivateKeyBackup,
  storePrivateKeyBackup,
  fetchPeerPublicKey,
  fetchStatusKey,
} from "../components/key";
import { toastError } from "../utils/toast";
import { getTombstoneText } from "../utils/getTombstoneText";
import {
  markMessageDeletedForMe,
  markMessageDeletedForEveryone
} from "../db/fsDB";
import {
  cachePublicKey,
  getPublicKey,

  encryptMessage,
  decryptMessage,
  decryptObject,
  decryptWithGroupKey,
  decryptObjectWithGroupKey,

  loadPrivateKey,
  loadPublicKeyJwk,

  generateKeyPair,
  saveKeyPair,
   savePrivateKey,

  loadGroupKey,
  saveGroupKey,
  decryptGroupKey,
  importGroupKey,

    decryptStatusKey,
    importStatusKey,
    saveStatusKey,
  loadStatusKey,

  encryptPrivateKeyBackup,
  decryptPrivateKeyBackup,

} from "../utils/Cryptoutils";

export function useSocketInit({
  currentUser,
  users,
  setUsers,
  setGroups,
  setMessages,
    messages,  
  setTypingUsers,
  setGroupTyping,
  setGroupMembers,
  setP2pOffers,
  setP2POrders,
  setMyAds,
  setSelectedItem,
  getUserKey,
  getGroupKey,
  setOrderTimers,
  selectedItem,
    isChatsVisible = true,
    onUsersReady,
onGroupsReady,
  onForceLogout,
}) {
  /* ───────────────────────── STATE / REFS ───────────────────────── */
  const [socket, setSocket] = useState(null);

  const plaintextPreviewRef = useRef({});
  const clientReadySentRef  = useRef(false);
const messagesRef = useRef({});

  const myChatId = currentUser?.chatInfo?.[0]?.chatId || null;

  useEffect(() => {
  messagesRef.current = messages || {};
}, [messages]);

 const selectedItemRef = useRef(selectedItem);
  const isChatsVisibleRef = useRef(isChatsVisible);
  useEffect(() => { selectedItemRef.current = selectedItem; }, [selectedItem]);
  useEffect(() => { isChatsVisibleRef.current = isChatsVisible; }, [isChatsVisible]);
/* ───────────────────────── SOCKET INIT ───────────────────────── */
useEffect(() => {
  if (!currentUser) return;

  const cleanup = initSocket(currentUser, {
    setUsers,
    setGroups,
    setMessages,
    setTypingUsers,
    setGroupTyping,
    setGroupMembers,
    setP2POffers: setP2pOffers,
    setP2POrders,
    setMyAds,
    setSelectedItem,
    getUserKey,
    getGroupKey,
    setOrderTimers,
    onUsersReady,
    onGroupsReady,
  });

  const sock = getSocket();
  setSocket(sock);

  return () => cleanup?.();
}, [currentUser]);

  /* ───────────────────────── CLIENT READY ───────────────────────── */
  useEffect(() => {
    if (!socket || !myChatId) return;

    const emitClientReady = () => {
      if (clientReadySentRef.current) return;

      clientReadySentRef.current = true;

      socket.emit("client-ready");

    };

    const onConnect = () => {
      clientReadySentRef.current = false;
      setTimeout(emitClientReady, 0);
    };

    socket.on("connect", onConnect);

    if (socket.connected) {
      setTimeout(emitClientReady, 0);
    }

    return () => {
      socket.off("connect", onConnect);
    };
  }, [socket, myChatId]);

/* ─────────────────────────
   STATUS KEY LOADER
───────────────────────── */

const ensureStatusKey =
async (
  ownerChatId
) => {

  if (!ownerChatId) {

    return null;

  }

  /* =========================================
     Already stored locally?
  ========================================= */

  let statusKey =
    await loadStatusKey(
      ownerChatId
    );

  if (statusKey) {

    return statusKey;

  }

  try {

    /* =========================================
       Fetch wrapped key from server
    ========================================= */

    const wrapped =
      await fetchStatusKey(
        ownerChatId
      );

    if (
      !wrapped?.wrappedKey
    ) {

      return null;

    }

    /* =========================================
       Load my E2EE private key
    ========================================= */

    const myPrivateKey =
      await loadPrivateKey(
        myChatId
      );

    if (!myPrivateKey) {

      return null;

    }

    /* =========================================
       Decrypt wrapped status key
    ========================================= */

    const rawKey =
      await decryptStatusKey(

        wrapped.wrappedKey,

        myPrivateKey

      );

    if (!rawKey) {

      return null;

    }

    /* =========================================
       Import CryptoKey
    ========================================= */

    statusKey =
      await importStatusKey(
        rawKey
      );

    if (!statusKey) {

      return null;

    }

    /* =========================================
       Persist to IndexedDB
    ========================================= */

    await saveStatusKey(

      ownerChatId,

      statusKey

    );

    return statusKey;

  }

  catch {

    return null;

  }

};

/* ─────────────────────────
   STATUS KEY GETTER
───────────────────────── */

const getStatusKey =
async (
  ownerChatId
) => {

  return await ensureStatusKey(
    ownerChatId
  );

};

  /* ───────────────────────── CORE SOCKET EVENTS ───────────────────────── */
  useEffect(() => {
    if (!socket) return;

    /* ===========================================================
       📥 RECEIVE MESSAGE (DM + GROUP — FIXED)
    =========================================================== */
socket.on("receive-message", async (data) => {
  try {
    const raw = data?.raw || null;

    const msgId =
      raw?.key?.msgId ||
      data?.msgId;

    const senderChatId =
      raw?.key?.senderChatId ||
      data?.senderChatId;

    const message =
      raw?.message ||
      data?.message;

    if (!msgId || !senderChatId || !message) return;

    const isFromMe = raw?.key?.fromMe === true;

const peerChatId = isFromMe
  ? raw?.key?.recipientChatId
  : senderChatId;

const chatKey = getUserKey(peerChatId);

    const myPrivateKey = await loadPrivateKey(myChatId);

    let text = null;
    let media = null;

    if (message?.encryptedText?.ciphertext && myPrivateKey) {
      text = await decryptMessage(
        message.encryptedText.ciphertext,
        message.encryptedText.iv,
        message.encryptedText.ephemeralPublicKey,
        myPrivateKey
      );
    }

    if (message?.encryptedMedia?.ciphertext && myPrivateKey) {
      media = await decryptObject(
        message.encryptedMedia.ciphertext,
        message.encryptedMedia.iv,
        message.encryptedMedia.ephemeralPublicKey,
        myPrivateKey
      );
    }

    const replyToMessageId =
      message?.reply?.msgId ||
      message?.replyToMessageId ||
      null;

    const replyPreviewEncrypted =
      message?.reply?.replyPreviewEncrypted ||
      message?.replyPreviewEncrypted ||
      null;

    let replyPreview = null;

    if (replyPreviewEncrypted && myPrivateKey) {
      try {
        const env = JSON.parse(replyPreviewEncrypted);

        const decrypted = await decryptMessage(
          env.ciphertext,
          env.iv,
          env.ephemeralPublicKey,
          myPrivateKey
        );

        if (decrypted) {
          try {
            replyPreview = JSON.parse(decrypted);
          } catch {
            replyPreview = { type: "text", value: decrypted };
          }
        }
      } catch (err) {
     
      }
    }

    const recipientChatId =
      raw?.key?.fromMe
        ? raw?.key?.recipientChatId
        : myChatId;
 const openItem = selectedItemRef.current;
    const isActiveChat =
      isChatsVisibleRef.current &&
      document.visibilityState === "visible" &&
      openItem?.type === "user" &&
      String(openItem.chatId) === String(peerChatId);
      const newMessage = {
  id: msgId,
  chatKey,
  senderChatId,
  recipientChatId,
  fromMe: raw?.key?.fromMe || false,

  system:
    data?.system ??
    raw?.system ??
    false,

  timestamp: data?.timestamp || Date.now(),
  seen: isActiveChat,
  seenAt: isActiveChat ? Date.now() : null,
  messageType: message?.encryptedMedia ? "media" : "text",
  text,
  media,
  encryptedText: message?.encryptedText ?? null,
  encryptedMedia: message?.encryptedMedia ?? null,
  replyToMessageId,
  replyPreviewEncrypted,
  replyPreview,
};

   const {
  text: _removedText,
  media: _removedMedia,
  replyPreview: _removedReplyPreview,
  ...dbMessage
} = newMessage;
   await saveMessage(dbMessage).catch(() => {});

setMessages((prev) => {
  const list = prev[chatKey] || [];
  if (list.some((m) => m.id === msgId)) return prev;
  return { ...prev, [chatKey]: [...list, newMessage] };
});


if (msgId && chatKey) {
  socket.emit("message-ack", {
    msgId,
    type: "dm-message",
  });
}
    setUsers((prev) =>
      prev.map((u) =>
     String(u.chatId) === String(peerChatId)
          ? {
              ...u,
              lastMessage: text || (media ? "📎 Media" : ""),
              lastActivityAt: newMessage.timestamp,
            }
          : u
      )
    );

  } catch (err) {
  
  }
});


socket.on("group-receive-message", async (data) => {
  try {
    const { groupId, message } = data;
    if (!groupId || !message) return;

    const chatKey = getGroupKey(groupId);
    const groupKey = await loadGroupKey(groupId);

    let text = null;
    let media = null;

    if (message.encryptedText?.ciphertext && groupKey) {
      text = await decryptWithGroupKey(
        message.encryptedText.ciphertext,
        message.encryptedText.iv,
        groupKey
      );
    }

   if (message.encryptedMedia?.ciphertext && groupKey) {
  media = await decryptObjectWithGroupKey(
    message.encryptedMedia.ciphertext,
    message.encryptedMedia.iv,
    groupKey
  );
}

let replyPreview = null;

if (message?.replyPreviewEncrypted && groupKey) {
  try {
    const env = JSON.parse(message.replyPreviewEncrypted);
    const decrypted = await decryptWithGroupKey(env.ciphertext, env.iv, groupKey);
    if (decrypted) {
      try {
        replyPreview = JSON.parse(decrypted);
      } catch {
        replyPreview = { type: "text", value: decrypted };
      }
    }
  } catch (err) {
  
  }
}
const openItem = selectedItemRef.current;
const isActiveGroup =
  isChatsVisibleRef.current &&
  document.visibilityState === "visible" &&
  openItem?.type === "group" &&
  String(openItem.groupId) === String(groupId);

   const newMessage = {
  id: message.msgId,
  chatKey,
  senderChatId: message.senderChatId,
  fromMe:
  typeof message?.fromMe === "boolean"
    ? message.fromMe
    : String(message.senderChatId) === String(myChatId),
  timestamp: message.timestamp || Date.now(),
  seen: isActiveGroup,
  seenAt: isActiveGroup ? Date.now() : null,
  messageType: message.messageType || "text",
  text,
  media,
  encryptedText: message.encryptedText ?? null,
  encryptedMedia: message.encryptedMedia ?? null,
  isGroup: true,
  groupId,
  replyToMessageId: message?.replyToMessageId || null,
  replyPreviewEncrypted: message?.replyPreviewEncrypted || null,
  replyPreview,
};

 const {
  text: _removedText,
  media: _removedMedia,
  replyPreview: _removedReplyPreview,
  ...dbMessage
} = newMessage;
   await saveMessage(dbMessage).catch(() => {});

setMessages((prev) => {
  const list = prev[chatKey] || [];
  if (list.some((m) => m.id === message.msgId)) return prev;
  return { ...prev, [chatKey]: [...list, newMessage] };
});

if (message.msgId && groupId) {
  socket.emit("group-message-ack", { msgId: message.msgId, groupId });
}

    setGroups((prev) =>
      prev.map((g) =>
        String(g.groupId) === String(groupId)
          ? {
              ...g,
              lastMessage: text || (media ? "📎 Media" : ""),
              lastActivityAt: newMessage.timestamp,
            }
          : g
      )
    );

  } catch (err) {

  }
});

socket.on("message-seen", ({ fromChatId, msgIds, seenAt }) => {
  const chatKey = getUserKey(fromChatId);
  setMessages((prev) => {
    const list = prev[chatKey] || [];
    const updated = list.map((m) =>
      msgIds.includes(m.id) ? { ...m, seen: true, seenAt } : m
    );
    return { ...prev, [chatKey]: updated };
  });
});

socket.on("group-message-seen", ({ groupId, msgIds = [], seenAt, seenBy }) => {
  const chatKey = getGroupKey(groupId);
  setMessages((prev) => {
    const list = prev[chatKey] || [];
    const updated = list.map((m) => {
      if (!msgIds.includes(m.id)) return m;
      return { ...m, seenBy: [...(m.seenBy || []), seenBy], seenAt };
    });
    return { ...prev, [chatKey]: updated };
  });
});

socket.on("unread-reset", ({ fromChatId }) => {
  setUsers((prev) =>
    prev.map((u) =>
      String(u.chatId) === String(fromChatId) ? { ...u, unreadCount: 0 } : u
    )
  );
});

socket.on("group-unread-reset", ({ groupId, unreadCount }) => {
  setGroups((prev) =>
    prev.map((g) =>
      String(g.groupId) === String(groupId) ? { ...g, unreadCount: unreadCount ?? 0 } : g
    )
  );
});

    /* ===========================================================
       USER GROUPS LIST
    =========================================================== */
let groupsReadySent = false;

socket.on("user-groups", async (groups) => {

  setGroups((prev) => {
    const incomingGroups = groups || [];
    const map = new Map(prev.map(g => [String(g.groupId), g]));
    incomingGroups.forEach((g) => {
      const existing = map.get(String(g.groupId));
      map.set(String(g.groupId), {
        ...existing,
        ...g,
        lastMessage:    existing?.lastMessage    ?? g.lastMessage,
        lastActivityAt: existing?.lastActivityAt ?? g.lastActivityAt,
      });
    });
    return Array.from(map.values());
  });

  setGroupMembers((prev) => {
    const updated = { ...prev };
    (groups || []).forEach((g) => {
      if (g.groupId && Array.isArray(g.members)) {
        updated[g.groupId] = g.members;
      }
    });
    return updated;
  });

 if (!groupsReadySent && typeof onGroupsReady === "function") {
  groupsReadySent = true;
  onGroupsReady();
}

  try {
    const myPrivateKey = await loadPrivateKey(myChatId);
    for (const g of groups || []) {
      if (g.encryptedGroupKey && myPrivateKey) {
        try {
          const rawKey = await decryptGroupKey(g.encryptedGroupKey, myPrivateKey);
          if (rawKey) {
            const cryptoKey = await importGroupKey(rawKey);
            if (!(cryptoKey instanceof CryptoKey)) {
            
              return;
            }
            await saveGroupKey(g.groupId, cryptoKey);
          }
        } catch (err) {
        
        }
      }
    }
  } catch (err) {
   
  }
});

socket.on("group-unread-count", ({ groupId, unreadCount, timestamp }) => {
  setGroups((prev) =>
    prev.map((g) =>
      String(g.groupId) === String(groupId)
        ? { ...g, unreadCount: unreadCount ?? 0, lastActivityAt: timestamp || g.lastActivityAt }
        : g
    )
  );
});

    /* ===========================================================
       GROUP PRESENCE
    =========================================================== */
    socket.on("group-user-online", ({ groupId, chatId }) => {
    });

    /* ===========================================================
   🪦 DM MESSAGE DELETED (GLOBAL)
=========================================================== */
socket.on("dm-message-deleted", async ({
  peerId,
  msgId,
  deletedBy,
  deletedByRole,
}) => {
  const chatKey = getUserKey(peerId);

  let computedTombstone = null; // 👈 capture for sidebar

  setMessages((prev) => {
    const list = prev[chatKey] || [];

    return {
      ...prev,
      [chatKey]: list.map((m) => {
        const id = m.id ?? m.messageId ?? m?.raw?.key?.msgId;
        if (id !== msgId) return m;

        const senderChatId =
          m.senderChatId ?? m?.raw?.key?.senderChatId;

        const tombstoneText = getTombstoneText({
          deletedBy,
          deletedByRole,
          myChatId,
          senderChatId,
        });

        // 👇 capture once
        if (!computedTombstone) {
          computedTombstone = tombstoneText;
        }

        return {
          ...m,
          isDeleted: true,
          deletedBy,
          deletedByRole,
          text: tombstoneText,
        };
      }),
    };
  });

  await markMessageDeletedForEveryone(msgId).catch(() => {});

  // ✅ use tombstone instead of hardcoded text
setUsers((prev) =>
  prev.map((u) =>
    String(u.chatId) === String(peerId)
      ? {
          ...u,
          lastMessage: { text: computedTombstone, isDeleted: true },
        }
      : u
  )
);
  socket.emit("message-ack", { msgId, type: "dm-message-deleted" });
});


/* ===========================================================
   🫥 DM MESSAGE HIDDEN (FOR ME)
=========================================================== */
socket.on("dm-message-hidden", async ({ peerId, msgId }) => {
  const chatKey = getUserKey(peerId);

  setMessages((prev) => {
    const list = prev[chatKey] || [];
    const filtered = list.filter(
      (m) => (m.id ?? m.messageId ?? m?.raw?.key?.msgId) !== msgId
    );

    // ── Update sidebar to reflect new last message ──────────
    const newest = filtered.length > 0 ? filtered[filtered.length - 1] : null;
    const sidebarPayload = newest
      ? newest.isDeleted
        ? { text: newest.text, isDeleted: true }
        : newest.text || (newest.media ? "📎 Media" : "")
      : "";

    setUsers((u) =>
      u.map((u) =>
        String(u.chatId) === String(peerId)
          ? { ...u, lastMessage: sidebarPayload }
          : u
      )
    );

    return { ...prev, [chatKey]: filtered };
  });

  await markMessageDeletedForMe(msgId, myChatId).catch(() => {});
  socket.emit("message-ack", { msgId, type: "dm-message-hidden" });
});


/* ===========================================================
   🪦 GROUP MESSAGE DELETED (GLOBAL)
=========================================================== */
socket.on("group-message-deleted", async ({
  groupId,
  msgId,
  deletedBy,
  deletedByRole,
}) => {
  const chatKey = getGroupKey(groupId);

  let computedTombstone = null;

  setMessages((prev) => {
    const list = prev[chatKey] || [];

    return {
      ...prev,
      [chatKey]: list.map((m) => {
        const id = m.id ?? m.messageId ?? m?.raw?.key?.msgId;
        if (id !== msgId) return m;

        const senderChatId =
          m.senderChatId ?? m?.raw?.key?.senderChatId;

        const tombstoneText = getTombstoneText({
          deletedBy,
          deletedByRole,
          myChatId,
          senderChatId,
        });

        if (!computedTombstone) {
          computedTombstone = tombstoneText;
        }

        return {
          ...m,
          isDeleted: true,
          deletedBy,
          deletedByRole,
          text: tombstoneText,
        };
      }),
    };
  });

  await markMessageDeletedForEveryone(msgId).catch(() => {});

  // ✅ use tombstone
 setGroups((prev) =>
  prev.map((g) =>
    String(g.groupId) === String(groupId)
      ? {
          ...g,
          lastMessage: { text: computedTombstone, isDeleted: true },
        }
      : g
  )
);
  socket.emit("group-message-ack", { msgId });
});


/* ===========================================================
   🫥 GROUP MESSAGE HIDDEN (FOR ME)
=========================================================== */
socket.on("group-message-hidden", async ({ groupId, msgId }) => {
  const chatKey = getGroupKey(groupId);

  setMessages((prev) => {
    const list = prev[chatKey] || [];
    const filtered = list.filter(
      (m) => (m.id ?? m.messageId ?? m?.raw?.key?.msgId) !== msgId
    );

    // ── Update sidebar to reflect new last message ──────────
    const newest = filtered.length > 0 ? filtered[filtered.length - 1] : null;
    const sidebarPayload = newest
      ? newest.isDeleted
        ? { text: newest.text, isDeleted: true }
        : newest.text || (newest.media ? "📎 Media" : "")
      : "";

    setGroups((g) =>
      g.map((g) =>
        String(g.groupId) === String(groupId)
          ? { ...g, lastMessage: sidebarPayload }
          : g
      )
    );

    return { ...prev, [chatKey]: filtered };
  });

  await markMessageDeletedForMe(msgId, myChatId).catch(() => {});
  socket.emit("group-message-ack", { msgId });
});

socket.on("dm-chat-cleared", ({ peerId, deletedMsgIds }) => {
  const chatKey = getUserKey(peerId);

  setMessages((prev) => {
    const list = prev[chatKey] || [];
    const idsToRemove = new Set(deletedMsgIds || []);
    // If server sent specific ids, remove only those; otherwise wipe the whole chat
    const filtered = idsToRemove.size > 0
      ? list.filter((m) => !idsToRemove.has(m.id ?? m.messageId ?? m?.raw?.key?.msgId))
      : [];
    return { ...prev, [chatKey]: filtered };
  });

  // Clear sidebar preview
  setUsers((prev) =>
    prev.map((u) =>
      String(u.chatId) === String(peerId)
        ? { ...u, lastMessage: "", lastActivityAt: u.lastActivityAt }
        : u
    )
  );
});

socket.on("group-chat-cleared", ({ groupId, deletedMsgIds }) => {
  const chatKey = getGroupKey(groupId);

  setMessages((prev) => {
    const list = prev[chatKey] || [];
    const idsToRemove = new Set(deletedMsgIds || []);
    const filtered = idsToRemove.size > 0
      ? list.filter((m) => !idsToRemove.has(m.id ?? m.messageId ?? m?.raw?.key?.msgId))
      : [];
    return { ...prev, [chatKey]: filtered };
  });

  setGroups((prev) =>
    prev.map((g) =>
      String(g.groupId) === String(groupId)
        ? { ...g, lastMessage: "", unreadCount: 0 }
        : g
    )
  );
});

     /* ===========================================================
       ✏️  DM MESSAGE EDITED
    =========================================================== */
    socket.on("dm-message-edited", async ({
      peerId,
      msgId,
      isEdited,
      ciphertext,
      iv,
      ephemeralPublicKey,
      newText,            // present only on the editing device (already in UI)
    }) => {
      const chatKey = getUserKey(peerId);
 
      let decryptedText = newText ?? null;
 
      // ── Decrypt the envelope ────────────────────────────────────────────
      if (ciphertext && iv && ephemeralPublicKey) {
        try {
          const myPrivateKey = await loadPrivateKey(myChatId);
 
          if (myPrivateKey) {
            // EPK may arrive as a serialised JSON string — normalise to object.
            let epkValue = ephemeralPublicKey;
            if (typeof epkValue === "string") {
              try { epkValue = JSON.parse(epkValue); } catch { /* already raw */ }
            }
 
            const plain = await decryptMessage(ciphertext, iv, epkValue, myPrivateKey);
            if (plain) decryptedText = plain;
          }
        } catch (err) {
         
        }
      } else if (ciphertext && iv && !ephemeralPublicKey) {
    
      }
 
      // ── Update in-memory message list ───────────────────────────────────
      setMessages((prev) => {
        const list = prev[chatKey] ?? [];
        return {
          ...prev,
          [chatKey]: list.map((m) => {
            const id = m?.id ?? m?.messageId ?? m?.raw?.key?.msgId;
            if (id !== msgId) return m;
            return {
              ...m,
              ...(decryptedText !== null ? { text: decryptedText } : {}),
              ...(ciphertext
                ? { encryptedText: { ciphertext, iv, ephemeralPublicKey: ephemeralPublicKey ?? null } }
                : {}),
              isEdited: true,
            };
          }),
        };
      });
 
      // ── FIX: Update chat-list sidebar preview ───────────────────────────
      // Only update when we have a real decrypted string. Avoids blanking
      // the preview on a device that couldn't decrypt (edge-case guard).
      if (decryptedText !== null) {
        setUsers((prev) =>
          prev.map((u) =>
            String(u.chatId) === String(peerId)
              ? { ...u, lastMessage: decryptedText }
              : u
          )
        );
      }
 
      // ── Persist updated envelope to IDB ─────────────────────────────────
      if (ciphertext) {
        saveMessage({
          id: msgId,
          encryptedText: { ciphertext, iv, ephemeralPublicKey: ephemeralPublicKey ?? null },
          isEdited: true,
        }).catch(() => {});
      }
        socket.emit("message-ack", { msgId, type: "dm-message-edited" });
    });
 
    /* ===========================================================
       ✏️  GROUP MESSAGE EDITED
    =========================================================== */
    socket.on("group-message-edited", async ({ groupId, msgId, newText, newCiphertext, newIv }) => {
      const chatKey = getGroupKey(groupId);
 
      let decryptedText = newText ?? null;
 
      // ── Decrypt if the server sent an encrypted update ──────────────────
      if (newCiphertext && newIv) {
        try {
          const groupKey = await loadGroupKey(groupId);
          if (groupKey) {
            const plain = await decryptWithGroupKey(newCiphertext, newIv, groupKey);
            if (plain) decryptedText = plain;
          }
        } catch (err) {
        
        }
      }
 
      // ── Update in-memory message list ───────────────────────────────────
      setMessages((prev) => {
        const list = prev[chatKey] ?? [];
        return {
          ...prev,
          [chatKey]: list.map((m) => {
            const id = m?.id ?? m?.messageId ?? m?.raw?.key?.msgId;
            if (id !== msgId) return m;
            return {
              ...m,
              ...(decryptedText !== null ? { text: decryptedText } : {}),
              ...(newCiphertext
                ? { encryptedText: { ciphertext: newCiphertext, iv: newIv, ephemeralPublicKey: null } }
                : {}),
              isEdited: true,
            };
          }),
        };
      });
 
      // ── FIX: Update chat-list sidebar preview ───────────────────────────
      if (decryptedText !== null) {
        setGroups((prev) =>
          prev.map((g) =>
            String(g.groupId) === String(groupId)
              ? { ...g, lastMessage: decryptedText }
              : g
          )
        );
      }
 
      // ── Persist to IDB ──────────────────────────────────────────────────
      const idbPayload = { id: msgId, isEdited: true };
      if (newCiphertext) {
        idbPayload.encryptedText = { ciphertext: newCiphertext, iv: newIv, ephemeralPublicKey: null };
      }
      saveMessage(idbPayload).catch(() => {});
        socket.emit("group-message-ack", { msgId });
    });

socket.on("message-sent-time", async ({ msgId, timestamp }) => {
  if (!msgId || !timestamp) return;
  const ts = new Date(timestamp).getTime();
  await saveMessage({ id: msgId, timestamp: ts }).catch(() => {});
});

socket.on("group-message-sent-time", async ({ msgId, groupId, timestamp }) => {
  if (!msgId || !timestamp) return;
  const ts = new Date(timestamp).getTime();
  await saveMessage({ id: msgId, timestamp: ts }).catch(() => {});
});

    /* ===========================================================
       ⏰ SCHEDULED MESSAGE — DELETED (server confirms hard delete)
       Fired only to the sender's own room.
    =========================================================== */
 // AFTER
socket.on("schedule:dm:deleted", ({ msgId }) => {
  if (!msgId) return;

  // ── 1. Find which chatKey owns this message BEFORE removing it ──────────
  // messagesRef holds the current snapshot without stale-closure issues.
  let ownerChatKey = null;
  for (const [key, list] of Object.entries(messagesRef.current || {})) {
    if (!key.startsWith("user-")) continue;
    if (Array.isArray(list) && list.some((m) => m.id === msgId)) {
      ownerChatKey = key;
      break;
    }
  }

  // ── 2. Remove from in-memory state ──────────────────────────────────────
  setMessages((prev) => {
    const next = { ...prev };
    for (const key of Object.keys(next)) {
      next[key] = next[key].filter((m) => m.id !== msgId);
    }
    return next;
  });

  // ── 3. Update sidebar preview for Device 2 ──────────────────────────────
  // After removal, find the next most-recent message in that chat and use
  // it as the new preview — exactly what _updatePreviewAfterDelete does on
  // Device 1 inside useScheduleAction.
  if (ownerChatKey) {
    const peerId = ownerChatKey.replace("user-", "");
    const remaining = (messagesRef.current[ownerChatKey] || [])
      .filter((m) => m.id !== msgId)
      .slice()
      .sort((a, b) => {
        const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return tb - ta;
      });

    const newest = remaining[0] ?? null;
    const lastMessage = newest
      ? newest.scheduledAt
        ? {
            text:        newest.text || "📎 Media",
            isDeleted:   false,
            isScheduled: true,
            scheduledAt: new Date(newest.scheduledAt).toISOString(),
          }
        : {
            text:        newest.text || (newest.media ? "📎 Media" : ""),
            isDeleted:   !!newest.isDeleted,
            isScheduled: false,
            scheduledAt: null,
          }
      : { text: "", isDeleted: false, isScheduled: false, scheduledAt: null };

    setUsers((prev) =>
      prev.map((u) =>
        String(u.chatId) === String(peerId)
          ? { ...u, lastMessage }
          : u
      )
    );
  }

  // ── 4. Delete from IDB on ALL devices (including Device 2) ──────────────
  deleteScheduledMessage(msgId).catch(() => {});
});

// AFTER
socket.on("schedule:group:deleted", ({ msgId }) => {
  if (!msgId) return;

  // ── 1. Find which chatKey owns this message BEFORE removing it ──────────
  let ownerChatKey = null;
  for (const [key, list] of Object.entries(messagesRef.current || {})) {
    if (!key.startsWith("group-")) continue;
    if (Array.isArray(list) && list.some((m) => m.id === msgId)) {
      ownerChatKey = key;
      break;
    }
  }

  // ── 2. Remove from in-memory state ──────────────────────────────────────
  setMessages((prev) => {
    const next = { ...prev };
    for (const key of Object.keys(next)) {
      next[key] = next[key].filter((m) => m.id !== msgId);
    }
    return next;
  });

  // ── 3. Update sidebar preview for Device 2 ──────────────────────────────
  if (ownerChatKey) {
    const groupId = ownerChatKey.replace("group-", "");
    const remaining = (messagesRef.current[ownerChatKey] || [])
      .filter((m) => m.id !== msgId)
      .slice()
      .sort((a, b) => {
        const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return tb - ta;
      });

    const newest = remaining[0] ?? null;
    const lastMessage = newest
      ? newest.scheduledAt
        ? {
            text:        newest.text || "📎 Media",
            isDeleted:   false,
            isScheduled: true,
            scheduledAt: new Date(newest.scheduledAt).toISOString(),
          }
        : {
            text:        newest.text || (newest.media ? "📎 Media" : ""),
            isDeleted:   !!newest.isDeleted,
            isScheduled: false,
            scheduledAt: null,
          }
      : { text: "", isDeleted: false, isScheduled: false, scheduledAt: null };

    setGroups((prev) =>
      prev.map((g) =>
        String(g.groupId) === String(groupId)
          ? { ...g, lastMessage }
          : g
      )
    );
  }

  // ── 4. Delete from IDB on ALL devices (including Device 2) ──────────────
  deleteScheduledMessage(msgId).catch(() => {});
});

    /* ===========================================================
       ⏰ SCHEDULED DM — UPDATED
       FIX: Decrypt the sender-copy envelope so the secondary
       device shows the new edited text immediately, not the old.
    =========================================================== */
    socket.on("schedule:dm:updated", async ({
      msgId,
      scheduledAt,
      // sender's own ciphertext envelope (server echoes the sender copy)
      ciphertext,
      iv,
      ephemeralPublicKey,
      encryptedMedia,
      replyToMessageId,
      replyPreviewEncrypted,
    }) => {
      if (!msgId) return;

      // ── 1. Decrypt the new sender-copy envelope ────────────────────────
      // Device 1 already has the plaintext from its optimistic update.
      // Device 2 arrives here with only the ciphertext — we must decrypt
      // it to get the new text and patch the message in state.
      let decryptedText = null;

      if (ciphertext && iv) {
        try {
          const myPrivateKey = await loadPrivateKey(myChatId);
          if (myPrivateKey) {
            let epkValue = ephemeralPublicKey;
            if (typeof epkValue === "string") {
              try { epkValue = JSON.parse(epkValue); } catch { /* already raw */ }
            }
            const plain = await decryptMessage(ciphertext, iv, epkValue, myPrivateKey);
            if (plain) decryptedText = plain;
          }
        } catch (err) {
   
        }
      }

      // ── 2. Patch messages state — update text + scheduledAt ────────────
      // This is the key fix: the old handler only patched scheduledAt and
      // encryptedMedia but never updated the decrypted text field, so the
      // secondary device kept showing the pre-edit message text.
      setMessages((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = next[key].map((m) => {
            if (m.id !== msgId) return m;
            return {
              ...m,
              ...(decryptedText !== null ? { text: decryptedText } : {}),
              ...(scheduledAt   ? { scheduledAt }   : {}),
              ...(encryptedMedia !== undefined ? { encryptedMedia } : {}),
              ...(replyToMessageId !== undefined ? { replyToMessageId } : {}),
              ...(replyPreviewEncrypted !== undefined ? { replyPreviewEncrypted } : {}),
              ...(ciphertext
                ? {
                    encryptedText: {
                      ciphertext,
                      iv,
                      ephemeralPublicKey: ephemeralPublicKey ?? null,
                    },
                  }
                : {}),
              isEdited: !!(ciphertext || decryptedText),
            };
          });
        }
        return next;
      });

      // ── 3. Update sidebar preview ──────────────────────────────────────
      // Only update the sidebar when we successfully decrypted the new text.
      if (decryptedText !== null && scheduledAt) {
        const scheduledIso = new Date(scheduledAt).toISOString();
        const newLastMessage = {
          text:        decryptedText,
          isDeleted:   false,
          isScheduled: true,
          scheduledAt: scheduledIso,
        };

        // Find which user's chat contains this msgId and update their preview.
        // We scan all chatKeys in messages state via the ref to avoid stale closure.
        const currentMessages = messagesRef.current || {};
        for (const [chatKey, list] of Object.entries(currentMessages)) {
          if (!chatKey.startsWith("user-")) continue;
          if (!Array.isArray(list)) continue;
          if (!list.some((m) => m.id === msgId)) continue;

          const peerId = chatKey.replace("user-", "");
          setUsers((prev) =>
            prev.map((u) =>
              String(u.chatId) === String(peerId)
                ? { ...u, lastMessage: newLastMessage }
                : u
            )
          );
          break;
        }
      }

      // ── 4. Persist updated sender-copy envelope to IDB ─────────────────
      if (ciphertext) {
        saveScheduledMessage({
          msgId,
          encryptedText: {
            ciphertext,
            iv,
            ephemeralPublicKey: ephemeralPublicKey ?? null,
          },
          ...(scheduledAt       ? { scheduledAt }       : {}),
          ...(encryptedMedia    !== undefined ? { encryptedMedia }    : {}),
          ...(replyToMessageId  !== undefined ? { replyToMessageId }  : {}),
          ...(replyPreviewEncrypted !== undefined ? { replyPreviewEncrypted } : {}),
          isEdited: true,
        }).catch(() => {});
      }
    });
 
  socket.on("schedule:group:updated", async ({
  msgId,
  groupId,
  scheduledAt,
  newCiphertext,
  newIv,
  encryptedMedia,
  replyToMessageId,
  replyPreviewEncrypted,
}) => {
  if (!msgId) return;

  /* ==========================================================
     1. Resolve group FIRST
  ========================================================== */

  let resolvedGroupId = groupId || null;

  if (!resolvedGroupId) {
    for (const [key, list] of Object.entries(messagesRef.current || {})) {
      if (!key.startsWith("group-")) continue;

      if (
        Array.isArray(list) &&
        list.some((m) => m.id === msgId)
      ) {
        resolvedGroupId = key.replace("group-", "");
        break;
      }
    }
  }

  if (!resolvedGroupId) {
    
    return;
  }


  /* ==========================================================
     2. Decrypt the new group-key-encrypted ciphertext
        FIX: the old handler only updated the sidebar preview
        but never patched the decrypted `text` field in
        messages state, so Device 2 kept showing the old text.
  ========================================================== */

  let decryptedText = null;

  if (newCiphertext && newIv) {
    try {
      const groupKey = await loadGroupKey(resolvedGroupId);

      if (groupKey) {
        const plain = await decryptWithGroupKey(newCiphertext, newIv, groupKey);
        if (plain) decryptedText = plain;

      }
    } catch (err) {
  
    }
  }

  /* ==========================================================
     3. Patch in-memory scheduled message — text + scheduledAt
        This is the core fix for Device 2.
  ========================================================== */

  setMessages((prev) => {
    const next = { ...prev };

    for (const key of Object.keys(next)) {
      next[key] = next[key].map((m) => {
        if (m.id !== msgId) return m;


        return {
          ...m,
          // ← FIX: update the human-readable text field so Device 2
          //   renders the new content, not the pre-edit plaintext.
          ...(decryptedText !== null ? { text: decryptedText } : {}),

          ...(scheduledAt && { scheduledAt }),

          ...(newCiphertext
            ? {
                encryptedText: {
                  ciphertext: newCiphertext,
                  iv: newIv,
                  ephemeralPublicKey: null,
                },
              }
            : {}),

          ...(encryptedMedia !== undefined
            ? { encryptedMedia }
            : {}),

          ...(replyToMessageId !== undefined
            ? { replyToMessageId }
            : {}),

          ...(replyPreviewEncrypted !== undefined
            ? { replyPreviewEncrypted }
            : {}),

          isEdited: !!(newCiphertext || decryptedText),
        };
      });
    }

    return next;
  });


  /* ==========================================================
     4. Update group sidebar preview
  ========================================================== */

  if (decryptedText !== null) {
    setGroups((prev) =>
      prev.map((g) => {
        if (
          String(g.groupId) !==
          String(resolvedGroupId)
        ) {
          return g;
        }


        return {
          ...g,
          lastMessage: {
            ...(typeof g.lastMessage === "object"
              ? g.lastMessage
              : {}),
            text: decryptedText,
            isScheduled: true,
            isDeleted: false,
            scheduledAt:
              scheduledAt ||
              g?.lastMessage?.scheduledAt,
          },
          lastActivityAt: Date.now(),
        };
      })
    );

  }


  /* ==========================================================
     5. Persist to SCHEDULED store
  ========================================================== */

  try {
    await saveScheduledMessage({
      msgId,
      groupId: resolvedGroupId,

      ...(scheduledAt && {
        scheduledAt,
      }),

      ...(newCiphertext && {
        encryptedText: {
          ciphertext: newCiphertext,
          iv: newIv,
          ephemeralPublicKey: null,
        },
      }),

      ...(encryptedMedia !== undefined && {
        encryptedMedia,
      }),

      ...(replyToMessageId !== undefined && {
        replyToMessageId,
      }),

      ...(replyPreviewEncrypted !== undefined && {
        replyPreviewEncrypted,
      }),

      isEdited: true,
      updatedAt: Date.now(),
    });


  } catch (err) {
   
  }
});

/* ===========================================================
   🚫 CHAT REMOVED (UNFOLLOW)
=========================================================== */

const handleChatRemoved = async ({ chatId }) => {

  if (!chatId) {
    return;
  }

  const chatKey =
    getUserKey(chatId);

  /* ----------------------------------------
     REMOVE USER FROM CHAT LIST
  ---------------------------------------- */

  setUsers((prev) =>
    prev.filter(
      (u) =>
        String(u.chatId) !==
        String(chatId)
    )
  );

  /* ----------------------------------------
     REMOVE IN-MEMORY MESSAGES
  ---------------------------------------- */

  setMessages((prev) => {

    const next = {
      ...prev,
    };

    delete next[
      chatKey
    ];

    return next;

  });

  /* ----------------------------------------
     REMOVE INDEXEDDB MESSAGES
  ---------------------------------------- */

  try {

    await deleteChatMessages(
      chatKey
    );

  } catch (err) {

  }

  /* ----------------------------------------
     CLOSE CHAT IF IT IS OPEN
  ---------------------------------------- */

  setSelectedItem(
    (current) => {

      if (

        current?.type ===
          "user" &&

        String(
          current.chatId
        ) ===
        String(chatId)

      ) {

        return null;

      }

      return current;

    }
  );

};

socket.on(
  "chat-removed",
  handleChatRemoved
);


    return () => {
      socket.off("receive-message");
      socket.off("group-receive-message");
      socket.off("user-groups");
      socket.off("group-user-online");
      socket.off("message-seen");
      socket.off("group-message-seen");
      socket.off("unread-reset");
      socket.off("group-unread-reset");
      socket.off("group-unread-count");
      socket.off("dm-message-deleted");
      socket.off("group-message-deleted");
      socket.off("dm-chat-cleared");
socket.off("group-chat-cleared");
      socket.off("dm-message-edited");
      socket.off("group-message-edited");
      socket.off("message-sent-time");
      socket.off("group-message-sent-time");
      // ── scheduled ──
      socket.off("schedule:dm:deleted");
      socket.off("schedule:group:deleted");
      socket.off("schedule:dm:updated");
      socket.off("schedule:group:updated");
       socket.off(
    "chat-removed",
    handleChatRemoved
  );
    };
}, [socket, getUserKey, getGroupKey, myChatId]);

useEffect(() => {
  if (!socket) return;

  let isLoggingOut = false;

  const handleForceLogout = ({ reason }) => {
    if (isLoggingOut) return;
    isLoggingOut = true;
    toastError("Session ended because you logged in with another device", {
      throttleKey: "force-logout",
      onClose: async () => {
        if (typeof onForceLogout === "function") {
          await onForceLogout();
        }
      },
    });
  };

  socket.on("force-logout", handleForceLogout);
  return () => socket.off("force-logout", handleForceLogout);
}, [socket, onForceLogout]);

 /* ───────────────────────── E2EE INIT ───────────────────────── */
useEffect(() => {

 if (!socket || !currentUser || !myChatId) return;

  const setupE2EE = async () => {

    try {


      /* =====================================================
         LOAD CACHED PUBLIC KEY
      ===================================================== */

      const localPubRaw =
        localStorage.getItem(
          `e2ee_pubkey_${myChatId}`
        );

      if (localPubRaw) {

        try {

          const parsed =
            JSON.parse(
              localPubRaw
            );

          cachePublicKey(
            myChatId,
            parsed
          );

        } catch (e) {


        }

      }

      /* =====================================================
         CHECK LOCAL PRIVATE KEY
      ===================================================== */

      let privateKey =
        await loadPrivateKey(
          myChatId
        );

      if (privateKey) {

        const publicKey =
          loadPublicKeyJwk(
            myChatId
          );

        if (publicKey) {

          cachePublicKey(
            myChatId,
            publicKey
          );

        }

        return;

      }

      /* =====================================================
         TRY RESTORE FROM SERVER
      ===================================================== */

      const backup =
        await fetchPrivateKeyBackup();

      const encryptedPrivateKey =
        backup?.encryptedPrivateKey;

      const salt =
        backup?.salt;

      const publicKey =
        backup?.publicKey;

      if (
        encryptedPrivateKey &&
        salt
      ) {

        const password =
          window.__E2EE_PASSWORD__;

        if (!password) {

          return;

        }

        const privateJwk =
          await decryptPrivateKeyBackup(
            encryptedPrivateKey,
            password,
            salt
          );

        if (!privateJwk) {

          return;

        }

        privateKey =
          await crypto.subtle.importKey(
            "jwk",
            privateJwk,
            {
              name: "ECDH",
              namedCurve: "P-256",
            },
            false,
            [
              "deriveKey",
              "deriveBits",
            ]
          );

        await savePrivateKey(
          myChatId,
          privateKey
        );

        if (publicKey) {

          localStorage.setItem(
            `e2ee_pubkey_${myChatId}`,
            JSON.stringify(
              publicKey
            )
          );

          cachePublicKey(
            myChatId,
            publicKey
          );

        }

        return;

      }

      /* =====================================================
         GENERATE NEW KEYPAIR
      ===================================================== */

      const {
        privateKey:
          newPrivateKey,
        publicJwk,
      } =
      await generateKeyPair();

      await saveKeyPair(
        myChatId,
        newPrivateKey,
        publicJwk
      );

      localStorage.setItem(
        `e2ee_pubkey_${myChatId}`,
        JSON.stringify(
          publicJwk
        )
      );

      cachePublicKey(
        myChatId,
        publicJwk
      );

      /* =====================================================
         BACKUP PRIVATE KEY
      ===================================================== */

      const password =
        window.__E2EE_PASSWORD__;

      if (!password) {

      } else {

        const privateJwk =
          await crypto.subtle.exportKey(
            "jwk",
            newPrivateKey
          );

        const backupData =
          await encryptPrivateKeyBackup(
            privateJwk,
            password
          );

        await storePrivateKeyBackup({

          encryptedPrivateKey:
            JSON.stringify({

              ciphertext:
                backupData.encryptedPrivateKey,

              iv:
                backupData.iv,

            }),

          salt:
            backupData.salt,

          publicKey:
            publicJwk,

        });


      }

    } catch (err) {

    }

  };

  setupE2EE();

}, [socket, currentUser, myChatId]);


useEffect(() => {

  const clearPassword =
  async () => {

    const dmKey =
      await loadPrivateKey(
        myChatId
      );

    if (dmKey) {

      window.__E2EE_PASSWORD__ =
        null;

    }

  };

  clearPassword();

}, [myChatId]);
/* ───────────────────────── PEER PUBLIC KEY HYDRATION ───────────────────────── */
useEffect(() => {
  if (!myChatId || !users?.length) return;

  const hydratePeerPublicKeys = async () => {
    const hydratedPeerIds = new Set();

    for (const user of users) {
      const peerChatId = user?.chatId;
      if (!peerChatId || String(peerChatId) === String(myChatId)) continue;
      if (hydratedPeerIds.has(String(peerChatId))) continue;

      const cached = getPublicKey(peerChatId);
      if (cached) { hydratedPeerIds.add(String(peerChatId)); continue; }

      const lsRaw = localStorage.getItem(`e2ee_pubkey_${peerChatId}`);
      if (lsRaw) {
        try {
          const parsed = JSON.parse(lsRaw);
          cachePublicKey(peerChatId, parsed);
          hydratedPeerIds.add(String(peerChatId));
          continue;
        } catch (e) {
       
        }
      }

      try {
        const serverPubKey = await fetchPeerPublicKey(peerChatId);
        if (serverPubKey) {
          localStorage.setItem(`e2ee_pubkey_${peerChatId}`, JSON.stringify(serverPubKey));
          cachePublicKey(peerChatId, serverPubKey);
        }
      } catch (err) {
    
      }

      hydratedPeerIds.add(String(peerChatId));
    }
  };

  hydratePeerPublicKeys();
}, [myChatId, users]);


/* ───────────────────────── UI HELPERS ───────────────────────── */

const setOutgoingPreview = (peerChatId, plaintext) => {
  if (peerChatId && typeof plaintext === "string") {
    plaintextPreviewRef.current[String(peerChatId)] = plaintext;
  }
};

const updateChatPreview = (peerChatId, plaintext, timestamp) => {
  if (!peerChatId || typeof plaintext !== "string") return;
  setUsers((prev) =>
    prev.map((u) =>
      String(u.chatId) === String(peerChatId)
        ? { ...u, lastMessage: plaintext, lastActivityAt: timestamp || u.lastActivityAt }
        : u
    )
  );
};

const cryptoFns = useMemo(() => ({

  cachePublicKey,
  getPublicKey,

  encryptMessage,
  decryptMessage,
  decryptObject,
  decryptWithGroupKey,
  decryptObjectWithGroupKey,

  loadPrivateKey,
  loadPublicKeyJwk,

  generateKeyPair,
  saveKeyPair,

  loadGroupKey,
  saveGroupKey,
  decryptGroupKey,
  importGroupKey,

  encryptPrivateKeyBackup,
  decryptPrivateKeyBackup,

}), []);

  /* ───────────────────────── RETURN ───────────────────────── */
  return {
    socket,
    setSocket,
    plaintextPreviewRef,
    setOutgoingPreview,
    updateChatPreview,
    cryptoFns,
    getStatusKey,
  };
}